import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  Flex,
  Box,
  ModalFooter
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { FaTruck } from 'react-icons/fa'
import { IoIosAddCircle } from 'react-icons/io'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'
import useFilters from '@hook/shared/use-filters'

import Filter from '@component/Shared/Filter'

import paramsHelper from '@helper/params'
import { getCategoriasFornecedores } from '@service/ParametroCompra'

interface IValue {
  label: string
  value: string
}

const CronPurchaseModalUserUpdate: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [usuario, setUsuario] = useState<IValue | null>(null)
  const { data } = useFilters()

  const [fornecedoresOriginal, setFornecedoresOriginal] = useState<any[]>([])
  const [fornecedoresOptions, setFornecedoresOptions] = useState<IValue[]>([])
  const [categoriasOptions, setCategoriasOptions] = useState<IValue[]>([])
  const { editDataUser, companieId, updateUserOb, selectedUser } =
    useParametrizationConfigPurchase()

  const [formParams, setFormParams] = useState<{
    fornecedorSelecionado: IValue[]
    categoria: IValue[]
  }>({
    fornecedorSelecionado: [],
    categoria: []
  })

  // Estado para controlar se algum select está aberto
  const [isSelectOpen, setIsSelectOpen] = useState(false)

  const [loadingFornecedores, setLoadingFornecedores] = useState(false)
  const [loadingCategorias, setLoadingCategorias] = useState(false)

  const fetchFornecedores = async () => {
    try {
      setLoadingFornecedores(true)
      const resp = await getCategoriasFornecedores()
      setFornecedoresOriginal(resp.data?.data)

      // Aplica distinct nos fornecedores baseado no supplierId
      const fornecedoresUnicos = resp.data?.data?.filter(
        (f: any, index: number, array: any[]) =>
          array.findIndex((item: any) => item.supplierId === f.supplierId) ===
          index
      )

      const fornecedores = fornecedoresUnicos?.map((f: any) => ({
        label: f.supplierId + '-' + f.supplierDescription,
        value: String(f.supplierId)
      }))
      setFornecedoresOptions(fornecedores)
    } catch (err) {
      console.error('Erro ao carregar fornecedores', err)
    } finally {
      setLoadingFornecedores(false)
    }
  }

  const preencherCamposEdicao = async () => {
    if (updateUserOb && updateUserOb.length > 0 && selectedUser) {
      const firstUser = updateUserOb[0]

      const user = data.users.find(
        u => String(u.value) === String(firstUser.usuarioId)
      )

      if (user) {
        setUsuario({
          label: user.label,
          value: String(user.value)
        })
      }

      const fornecedor = {
        label: `${firstUser.fornecedorId} - ${firstUser.fornecedorNome}`,
        value: String(firstUser.fornecedorId)
      }

      await handleFornecedorSelecionadoChange(fornecedor)

      // Carrega todas as categorias do usuário
      const categorias: IValue[] = updateUserOb.map(userData => ({
        label: userData.categoriaCompactaNome,
        value: String(userData.categoriaCompactaId)
      }))

      setFormParams({
        fornecedorSelecionado: [fornecedor],
        categoria: categorias
      })
    }
  }

  useEffect(() => {
    if (!isOpen) return
    fetchFornecedores()
  }, [isOpen])

  useEffect(() => {
    if (isOpen && fornecedoresOriginal.length > 0) {
      preencherCamposEdicao()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fornecedoresOriginal])

  const handleFormChange = (
    field: keyof typeof formParams,
    value: IValue[]
  ) => {
    setFormParams(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleFornecedorSelecionadoChange = async (value: IValue) => {
    const valueS = [value]
    handleFormChange('fornecedorSelecionado', valueS)
    handleFormChange('categoria', [])

    if (valueS.length === 0) {
      setCategoriasOptions([])
      return
    }

    try {
      setLoadingCategorias(true)
      const categoriasFiltradas: IValue[] = []
      const fornecedoresMap: Record<string, IValue[]> = {}

      fornecedoresOriginal.forEach(cat => {
        const isRelacionado = valueS.some(
          v => v.value === String(cat.supplierId)
        )

        if (isRelacionado) {
          const categoriaId = String(cat.compactCategoryId)

          if (!categoriasFiltradas.some(c => c.value === categoriaId)) {
            categoriasFiltradas.push({
              label: cat.compactCategoryDescription,
              value: categoriaId
            })
          }

          if (!fornecedoresMap[categoriaId]) {
            fornecedoresMap[categoriaId] = []
          }

          fornecedoresMap[categoriaId].push({
            label: cat.supplierDescription,
            value: String(cat.supplierId)
          })
        }
      })

      setCategoriasOptions(categoriasFiltradas)
    } catch (err) {
      console.error('Erro ao buscar categorias por fornecedor', err)
    } finally {
      setLoadingCategorias(false)
    }
  }

  const clearForm = () => {
    setFormParams({
      fornecedorSelecionado: [],
      categoria: []
    })
    setUsuario(null)
    setCategoriasOptions([])
  }

  // Validar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    return (
      usuario && // Usuário selecionado
      formParams.fornecedorSelecionado.length > 0 && // Fornecedor selecionado
      formParams.categoria.length > 0 // Categoria compacta selecionada
    )
  }

  const handleSubmit = async () => {
    if (
      !usuario ||
      formParams.fornecedorSelecionado.length === 0 ||
      formParams.categoria.length === 0
    ) {
      return
    }

    const categoriasCompactasIds = paramsHelper.parseNumber(
      formParams.categoria
    )

    try {
      await editDataUser({
        fornecedorId: Number(formParams.fornecedorSelecionado[0].value),
        categoriasCompactasIds,
        empresaId: companieId,
        usuarioId: usuario.value,
        idUsuarioCompras:
          updateUserOb && updateUserOb.length > 0
            ? updateUserOb[0].idUsuarioCompras
            : selectedUser
      })

      onClose()
      clearForm()
    } catch (error) {
      console.error('Erro ao editar usuário:', error)
    }
  }

  return (
    <>
      <Button
        fontSize={14}
        size="sm"
        leftIcon={<IoIosAddCircle />}
        colorScheme="blue"
        variant="outline"
        onClick={onOpen}
      >
        {updateUserOb && updateUserOb.length > 0 ? 'Editar' : 'Adicionar'}
      </Button>

      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose()
          clearForm()
        }}
        scrollBehavior="inside"
        motionPreset="slideInBottom"
        size={isSelectOpen ? 'xl' : 'xl'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            paddingTop={2}
            paddingBottom={2}
            paddingLeft={4}
            paddingRight={4}
            textColor={'gray.200'}
            background={'#1d5840'}
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex>
              <Box p={2}>
                <FaTruck />
              </Box>
              <Box p={2} pt={1}>
                {updateUserOb && updateUserOb.length > 0
                  ? 'Editar Usuário Cronograma'
                  : 'Criar Comprador Cronograma'}
              </Box>
            </Flex>
          </ModalHeader>

          <ModalCloseButton />
          <ModalBody
            bg="white"
            pt={0}
            py={4}
            minH={isSelectOpen ? '300px' : '300px'}
          >
            <FormControl isRequired>
              <Filter
                label="Usuário"
                options={data.users}
                isDisabled={true}
                isMulti={false}
                value={usuario ? [usuario] : []}
                onChange={value => {
                  const selected = Array.isArray(value) ? value[0] : value
                  setUsuario(selected as IValue)
                }}
                onMenuOpen={() => setIsSelectOpen(true)}
                onMenuClose={() => setIsSelectOpen(false)}
              />
            </FormControl>

            <br />

            <FormControl isRequired>
              <Filter
                label="Fornecedor"
                options={fornecedoresOptions}
                isLoading={loadingFornecedores}
                isDisabled={true}
                isMulti={false}
                value={formParams.fornecedorSelecionado}
                onChange={value => {
                  // eslint-disable-next-line no-void
                  void handleFornecedorSelecionadoChange(value as IValue)
                }}
                onMenuOpen={() => setIsSelectOpen(true)}
                onMenuClose={() => setIsSelectOpen(false)}
              />
            </FormControl>

            <br />

            <FormControl isRequired>
              <Filter
                label="Categoria Compacta"
                options={categoriasOptions}
                isLoading={loadingCategorias}
                isMulti={true}
                isDisabled={formParams.fornecedorSelecionado.length === 0}
                value={formParams.categoria}
                onChange={value =>
                  handleFormChange('categoria', value as IValue[])
                }
                onMenuOpen={() => setIsSelectOpen(true)}
                onMenuClose={() => setIsSelectOpen(false)}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter
            bg="white"
            borderBottomLeftRadius={5}
            borderBottomRightRadius={5}
          >
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                onClose()
                clearForm()
              }}
            >
              Fechar
            </Button>
            <Button
              bg="green.600"
              color="#fff"
              isDisabled={!isFormValid()}
              onClick={handleSubmit}
              _hover={!isFormValid() ? {} : { bg: 'green.500' }}
              _active={{ bg: 'green.500' }}
              _disabled={{ bg: 'gray.300', color: 'gray.500', _hover: {} }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default CronPurchaseModalUserUpdate
